import i18n from "../i18n";
import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, Barcode, Camera, LogIn, LogOut, PawPrint, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { normalizedRole } from "@/lib/routeHome";

export default function ScanPage() {
    const { user } = useAuth();
    const canClockEmployees = ["owner", "farm manager", "manager"].includes(normalizedRole(user));
    const [lastCode, setLastCode] = useState("");
    const [result, setResult] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanMode, setScanMode] = useState("qr");
    const [manual, setManual] = useState("");
    const nav = useNavigate();
    const scannerRef = useRef(null);
    const readerId = "qr-reader-div";

    const stopScan = async () => {
        try {
            if (scannerRef.current) {
                await scannerRef.current.stop();
                await scannerRef.current.clear();
                scannerRef.current = null;
            }
        } catch (err) { console.warn("QR scanner stop failed:", err); }
        setScanning(false);
    };

    const startScan = async () => {
        setScanning(true);
        try {
            const html5QrCode = new Html5Qrcode(readerId);
            scannerRef.current = html5QrCode;
            const formatsToSupport = scanMode === "barcode"
                ? [
                    Html5QrcodeSupportedFormats.CODE_128,
                    Html5QrcodeSupportedFormats.CODE_39,
                    Html5QrcodeSupportedFormats.EAN_13,
                    Html5QrcodeSupportedFormats.EAN_8,
                    Html5QrcodeSupportedFormats.UPC_A,
                    Html5QrcodeSupportedFormats.UPC_E,
                ]
                : [Html5QrcodeSupportedFormats.QR_CODE];
            await html5QrCode.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 250, height: 250 }, formatsToSupport },
                async (decoded) => {
                    if (decoded === lastCode) return;
                    setLastCode(decoded);
                    await lookup(decoded);
                    await stopScan();
                },
                () => { /* silent scan errors */ }
            );
        } catch (e) {
            toast.error("Camera access denied or unavailable");
            setScanning(false);
        }
    };

    useEffect(() => () => { stopScan(); }, []);

    const lookup = async (code) => {
        try {
            const { data } = await api.get(`/qr/lookup?code=${encodeURIComponent(code)}`);
            setResult(data);
            if (data.type === "animal") toast.success(`Animal found: ${data.animal.tag_id}`);
            else if (data.type === "employee") toast.success(`Employee: ${data.employee.name}`);
            else toast.error("Code not recognized");
        } catch (e) { toast.error("Lookup failed"); }
    };

    const clockAction = async (action) => {
        if (result?.type !== "employee") return;
        try {
            const { data } = await api.post("/attendance/clock", { action, employee_code: result.employee.employee_code });
            toast.success(`${action === "in" ? "Clocked in" : "Clocked out"}: ${data.employee}`);
            setResult(null); setLastCode("");
        } catch (e) { toast.error(e.response?.data?.detail || "Clock action failed"); }
    };

    const goToAnimal = () => {
        if (result?.type !== "animal") return;
        nav(`/animals/${result.animal.id}`);
    };

    return (
        <div className="space-y-6" data-testid="scan-page">
            <div className="flex items-center gap-3">
                <QrCode className="w-8 h-8 text-primary" />
                <div>
                    <div className="tiny-label">{i18n.t("batch4.a703ae27575c")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch4.2b7e3ad7d073")}</h1>
                    <p className="text-muted-foreground mt-2">{i18n.t("batch4.03c2571f560f")}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 space-y-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2"><Camera className="w-5 h-5" /> {i18n.t("batch4.dcba9bbe1198")}</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            type="button"
                            variant={scanMode === "qr" ? "default" : "outline"}
                            onClick={() => setScanMode("qr")}
                            disabled={scanning}
                            data-testid="scan-mode-qr"
                        >
                            <QrCode className="w-4 h-4 mr-2" />
                            {i18n.t("batch4.45e7a1c379c7")}
                        </Button>
                        <Button
                            type="button"
                            variant={scanMode === "barcode" ? "default" : "outline"}
                            onClick={() => setScanMode("barcode")}
                            disabled={scanning}
                            data-testid="scan-mode-barcode"
                        >
                            <Barcode className="w-4 h-4 mr-2" />
                            {i18n.t("batch4.8e54dc5331b0")}
                        </Button>
                    </div>
                    <div id={readerId} className="rounded-xl overflow-hidden border border-border bg-black/5 min-h-[300px]" />
                    <div className="flex gap-3">
                        {!scanning ? (
                            <Button onClick={startScan} className="h-12 flex-1" data-testid="start-scan-btn"><Camera className="w-4 h-4 mr-2" /> {i18n.t("batch4.7014243c1874")}</Button>
                        ) : (
                            <Button onClick={stopScan} variant="destructive" className="h-12 flex-1">{i18n.t("batch4.9e253470c876")}</Button>
                        )}
                    </div>
                    <div className="pt-3 border-t border-border">
                        <div className="tiny-label mb-2">{i18n.t("batch4.a416c837d99b")}</div>
                        <div className="flex gap-2">
                            <Input value={manual} onChange={e => setManual(e.target.value)} placeholder={i18n.t("batch4.7a4690e818d2")} className="h-12" data-testid="manual-code-input"/>
                            <Button onClick={() => lookup(manual)} className="h-12" data-testid="manual-lookup-btn">{i18n.t("batch4.7015ab4fc4ea")}</Button>
                        </div>
                    </div>
                </Card>

                <Card className="p-6 space-y-4">
                    <h3 className="text-xl font-semibold">{i18n.t("batch4.5faa59d4bc37")}</h3>
                    {!result ? (
                        <div className="text-muted-foreground text-center py-12">{i18n.t("batch4.f9060f3f823d")}</div>
                    ) : result.type === "animal" ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-14 h-14 rounded-xl grid place-items-center text-3xl" style={{backgroundColor: (result.species?.color || "#eee") + "40"}}>{result.species?.icon || "🐾"}</div>
                                <div>
                                    <div className="tiny-label">{i18n.t("batch4.a8f71f6ec3cd")} {result.species?.name}</div>
                                    <div className="font-mono text-xl font-bold">{result.animal.tag_id}</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div><span className="text-muted-foreground">{i18n.t("batch4.71dd2eff9b4d")}</span> {result.animal.name || "—"}</div>
                                <div><span className="text-muted-foreground">{i18n.t("batch4.346c87feb190")}</span> {result.animal.breed || "—"}</div>
                                <div><span className="text-muted-foreground">{i18n.t("batch4.e5af0395ec56")}</span> {result.animal.sex}</div>
                                <div><span className="text-muted-foreground">{i18n.t("batch4.11dc9e195292")}</span> {result.animal.status}</div>
                                <div><span className="text-muted-foreground">{i18n.t("batch4.1db775fdebdd")}</span> {result.animal.pen || "—"}</div>
                            </div>
                            <Button onClick={goToAnimal} className="h-12 w-full" data-testid="goto-animal-btn"><PawPrint className="w-4 h-4 mr-2" /> {i18n.t("batch4.b7469682e86f")}</Button>
                        </div>
                    ) : result.type === "employee" ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-14 h-14 rounded-full bg-accent text-accent-foreground grid place-items-center text-xl font-bold">{(result.employee.name || "?").slice(0,1)}</div>
                                <div>
                                    <div className="tiny-label">{i18n.t("batch4.079711ea16f3")}</div>
                                    <div className="font-bold text-xl">{result.employee.name}</div>
                                    <div className="text-sm text-muted-foreground">{result.employee.position} · {result.employee.employee_code}</div>
                                </div>
                            </div>
                            {canClockEmployees ? <div className="grid grid-cols-2 gap-2">
                                <Button onClick={() => clockAction("in")} className="h-14 bg-primary" data-testid="clock-in-btn"><LogIn className="w-5 h-5 mr-2" /> {i18n.t("batch4.8d3e921408c8")}</Button>
                                <Button onClick={() => clockAction("out")} variant="outline" className="h-14" data-testid="clock-out-btn"><LogOut className="w-5 h-5 mr-2" /> {i18n.t("batch4.7f9bf082bf6a")}</Button>
                            </div> : <div className="text-sm text-muted-foreground" data-testid="employee-clock-restricted">{i18n.t("batch4.1968649bce0e")}</div>}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="font-mono text-2xl">{result.code}</div>
                            <div className="text-destructive mt-2">{i18n.t("batch4.f456d73d894a")}</div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
